<template>
<main class="bg-slate-100 pb-10 min-h-screen">
  <div class="mx-auto max-w-4xl px-4 sm:px-6 lg:px-8">
    <p v-show="!formState.loadError && !formState.registration" class="text-slate-600 text-center mt-8">
      Loading registration details, please wait...
    </p>

    <ErrorMessage :formError="formState.loadError" class="mt-3" />

    <!-- Back to List -->
    <div v-if="eventIdList > 0" class="mt-3">
      <a :href="'/registration-ticket/list/' + eventIdList" class="inline-flex items-center gap-1 rounded-lg py-1 text-sm font-medium text-slate-600 transition hover:bg-slate-200/60 hover:text-slate-900">
        <span class="text-base leading-none">&larr;</span> <span>Back to delegates list</span>
      </a>
    </div>

    <!-- Delegates -->
    <div v-if="formState.registration?.delegates" class="mt-3">
      <!-- Add Delegate Button -->
      <div class="flex items-center justify-between mb-2">
        <h3 class="text-lg font-semibold text-slate-800">Delegates</h3>
        <button
          type="button"
          @click="formState.showAddDelegate = true"
          class="flex items-center px-2 py-1 border border-slate-300 rounded-md bg-white hover:bg-slate-50 text-xs font-semibold text-slate-700"
          title="Add Delegate"
        >
          <span class="text-lg font-bold mr-1">+</span> Add
        </button>
      </div>

      <div class="bg-white border border-slate-200 rounded-2xl shadow-sm p-1 sm:p-2">
        <div
          v-for="item in sortedDelegates"
          :key="item.id"
          class="border-b border-slate-200 last:border-b-0 hover:bg-gray-100"
        >
          <div class="flex items-center gap-3 px-3 py-2.5 sm:px-3.5">
            <div class="relative flex-shrink-0 flex h-8 w-8 items-center justify-center self-start">
              <button
                type="button"
                @click.stop="openDelegateActions(item)"
                class="inline-flex h-8 w-8 items-center justify-center rounded-lg text-slate-500 transition hover:bg-slate-100 hover:text-slate-700"
                aria-label="Delegate actions"
                title="Delegate actions"
              >
                <EllipsisVerticalIcon class="h-5 w-5" />
              </button>

              <div
                v-if="formState.actionMenuFor === item.id"
                class="absolute left-0 top-9 z-20 min-w-[140px] rounded-xl border border-slate-200 bg-white p-1 shadow-lg"
              >
                <button
                  type="button"
                  @click.stop="beginMakeChild(item)"
                  class="block w-full rounded-lg px-3 py-2 text-left text-xs font-semibold text-slate-700 transition hover:bg-slate-50"
                >
                  Make Child
                </button>
                <button
                  type="button"
                  @click.stop="beginUpdateColour(item)"
                  class="block w-full rounded-lg px-3 py-2 text-left text-xs font-semibold text-slate-700 transition hover:bg-slate-50"
                >
                  Update Colour
                </button>
              </div>
            </div>

            <button
              class="group flex-1 min-w-0 text-left transition"
              @click.prevent="changeRegistered(item)"
              :aria-disabled="formState.loading"
              :tabindex="formState.loading ? -1 : 0"
              type="button"
            >
              <div class="min-w-0">
                <h3 :class="[item.cancelled ? 'line-through text-slate-400' : 'text-slate-900', 'truncate text-sm font-semibold sm:text-base']">
                  {{ item.firstName }} {{ item.lastName }}
                </h3>

                <div class="mt-0.5 text-xs text-slate-500">
                  {{ item.ticketTitle }}
                  <span v-if="item.cancelled" class="ml-2 rounded-full bg-slate-200 px-2 py-0.5 text-xs font-medium text-slate-600">
                    Cancelled
                  </span>
                </div>

                <div class="mt-0.5 space-y-0.5 text-xs text-slate-500">
                  <p v-if="item.child || item.age || item.gender">{{ item.child ? formatAgeGender(item) : 'Adult' }}</p>
                  <p v-if="item.wristband || item.colour || item.colourCode">
                    <span v-if="item.wristband">Band: {{ item.wristband }}</span>
                    <span v-if="item.wristband && (item.colour || item.colourCode)" class="mx-2 text-slate-300">|</span>
                    <span v-if="item.colour || item.colourCode">
                      {{ item.colour || 'Band' }}<span v-if="item.colourCode">-{{ String(item.colourCode).padStart(3, '0') }}</span>
                    </span>
                  </p>
                </div>
              </div>
            </button>

            <button
              type="button"
              @click.prevent="changeRegistered(item)"
              :aria-disabled="formState.loading"
              class="flex-shrink-0 inline-flex h-11 w-11 items-center justify-center rounded-full transition"
              :title="item.registered ? 'Checked in' : 'Not checked in'"
            >
              <span
                v-if="item.registered"
                class="inline-flex h-10 w-10 items-center justify-center rounded-full border-2 border-slate-900 bg-gray-900"
              >
                <CheckIcon class="h-6 w-6 text-white" />
              </span>
              <span
                v-else
                class="inline-flex h-10 w-10 items-center justify-center rounded-full border-2 border-slate-400 bg-white"
              >
                <XMarkIcon class="h-6 w-6 text-gray-300" />
              </span>
            </button>
          </div>
        </div>
      </div>
    </div>

    <!-- Checkout Info -->
    <div v-if="formState.registration?.checkout" class="mt-6">
      <h3 class="text-lg font-semibold text-slate-800 mb-2 flex items-center gap-2">
        Checkout Info
      </h3>
      <div class="bg-white border border-slate-200 rounded-2xl shadow-sm p-4 space-y-1">
        <div class="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2">
          <div>
            <p class="text-base font-bold">{{ formState.registration.checkout[0].firstName }} {{ formState.registration.checkout[0].lastName }}</p>
            <p class="text-sm text-slate-700">{{ formState.registration.checkout[0].email }}</p>
            <p v-if="formState.registration.checkout[0].tel" class="text-sm text-slate-700">Tel: {{ formState.registration.checkout[0].tel }}</p>
          </div>
          <div>
            <p class="text-xs font-bold text-slate-500">Order ID</p>
            <p class="text-xs text-slate-700 font-mono break-all">#{{ formState.registration.checkout[0].checkoutId }}</p>
          </div>
        </div>
        <div class="flex flex-wrap gap-4 mt-1 text-xs text-slate-700">
          <div v-if="formState.registration.checkout[0].confirmedAt">
            <p class="font-semibold text-slate-500">Confirmed At</p>
            <p>{{ $filters.dateLon(formState.registration.checkout[0].confirmedAt) }}</p>
          </div>
          <div v-if="formState.registration.checkout[0].cancelledAt">
            <p class="font-semibold text-slate-500">Cancelled At</p>
            <p>{{ $filters.dateLon(formState.registration.checkout[0].cancelledAt) }}</p>
          </div>
          <div v-if="formState.registration.checkout[0].isPartner">
            <p class="font-semibold text-slate-500">Partner</p>
          </div>
          <div v-if="formState.registration.checkout[0].country?.toLowerCase() !== 'gb'">
            <p class="font-semibold text-slate-500">International</p>
            <p>{{ formState.registration.checkout[0].country }}</p>
          </div>
        </div>
      </div>
    </div>

    <!-- Attendance Options -->
    <div v-if="formState.registration?.attendanceOptions" class="mt-6">
      <h3 class="text-lg font-semibold text-slate-800 mb-2">Session Attendance</h3>
      <div class="bg-white border border-slate-200 rounded-2xl shadow-sm p-2 sm:p-4 space-y-2">
        <div
          v-for="option in formState.registration.attendanceOptions"
          :key="option.id"
          class="border-b border-slate-100 pb-3 last:border-b-0"
        >
          <div class="flex items-start text-sm text-slate-700">
            <component
              :is="option.attending == null ? 'QuestionMarkCircleIcon' : (option.attending ? 'CheckIcon' : 'XMarkIcon')"
              :class="option.attending == null ? 'text-red-500' : option.attending ? 'text-green-500' : 'text-red-500'"
              class="h-5 w-5 mr-2 flex-shrink-0"
            />
            <span>
              {{ option.title }} - {{ $filters.dateTimeMed(option.publishedAt) }}
            </span>
          </div>
        </div>
      </div>
    </div>

    <!-- Loader Popover -->
    <transition name="fade">
      <div
        v-if="formState.loading"
        class="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-30"
      >
        <div class="bg-white rounded-lg shadow-lg px-6 py-4 flex items-center gap-3">
          <svg class="animate-spin h-6 w-6 text-blue-600" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
            <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
            <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8z"></path>
          </svg>
          <span class="text-blue-700 font-semibold">Updating registration...</span>
        </div>
      </div>
    </transition>

    <!-- Make Child Modal (edit First/Last Name, Age and Gender only, gender = M or F) -->
    <transition name="fade">
      <div
        v-if="formState.showMakeChild && formState.selectedDelegate"
        class="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-30"
      >
        <div class="bg-white rounded-lg shadow-lg px-6 py-6 w-full max-w-sm">
          <h3 class="text-lg font-bold mb-4">Make Child</h3>
          <form @submit.prevent="submitMakeChild">
            <div class="mb-3">
              <label class="block text-xs font-semibold text-gray-700 mb-1">First Name</label>
              <input v-model="formState.selectedDelegate.firstName" type="text" class="w-full border rounded px-2 py-1 text-sm" required />
            </div>
            <div class="mb-3">
              <label class="block text-xs font-semibold text-gray-700 mb-1">Last Name</label>
              <input v-model="formState.selectedDelegate.lastName" type="text" class="w-full border rounded px-2 py-1 text-sm" required />
            </div>
            <div class="mb-3">
              <label class="block text-xs font-semibold text-gray-700 mb-1">Age</label>
              <input v-model="formState.selectedDelegate.age" type="number" min="0" class="w-full border rounded px-2 py-1 text-sm" required />
            </div>
            <div class="mb-4">
              <label class="block text-xs font-semibold text-gray-700 mb-1">Gender</label>
              <select v-model="formState.selectedDelegate.gender" class="w-full border rounded px-2 py-1 text-sm" required>
                <option disabled value="">Select gender</option>
                <option value="F">Girl</option>
                <option value="M">Boy</option>
              </select>
            </div>
            <div class="flex justify-end gap-2">
              <button type="button" @click="formState.showMakeChild = false" class="px-3 py-1 rounded bg-gray-200 hover:bg-gray-300 text-gray-700 text-sm">Cancel</button>
              <button type="submit" class="px-3 py-1 rounded bg-blue-600 hover:bg-blue-700 text-white text-sm font-semibold">Save</button>
            </div>
          </form>
        </div>
      </div>
    </transition>

    <!-- Update Colour Modal -->
    <transition name="fade">
      <div
        v-if="formState.showUpdateColour && formState.selectedDelegate"
        class="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-30"
      >
        <div class="bg-white rounded-lg shadow-lg px-6 py-6 w-full max-w-sm">
          <h3 class="text-lg font-bold mb-4">Update Colour</h3>
          <form @submit.prevent="submitUpdateColour">
            <div class="mb-3">
              <label class="block text-xs font-semibold text-gray-700 mb-1">Colour</label>
              <select v-model="formState.selectedColour" class="w-full border rounded px-2 py-1 text-sm" required>
                <option disabled value="">Select colour</option>
                <option value="Orange">Orange (6)</option>
                <option value="Gold">Gold (7)</option>
                <option value="Blue">Blue (8)</option>
                <option value="Pink">Pink (9)</option>
                <option value="Red">Red (10)</option>
                <option value="Yellow">Yellow (11)</option>
                <option value="Purple">Purple (12)</option>
                <option value="Silver">Silver (13)</option>
                <option value="Green">Green (14)</option>
                <option value="White">White (15)</option>
                <option value="Turquoise">Turquoise (16)</option>
              </select>
            </div>
            <div class="mb-4">
              <label class="block text-xs font-semibold text-gray-700 mb-1">Colour Code</label>
              <input v-model="formState.selectedColourCode" type="number" min="0" class="w-full border rounded px-2 py-1 text-sm" required />
            </div>
            <div class="flex justify-end gap-2">
              <button type="button" @click="formState.showUpdateColour = false" class="px-3 py-1 rounded bg-gray-200 hover:bg-gray-300 text-gray-700 text-sm">Cancel</button>
              <button type="submit" class="px-3 py-1 rounded bg-blue-600 hover:bg-blue-700 text-white text-sm font-semibold">Save</button>
            </div>
          </form>
        </div>
      </div>
    </transition>

    <!-- Add Delegate Modal -->
    <transition name="fade">
      <div
        v-if="formState.showAddDelegate"
        class="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-30"
      >
        <div class="bg-white rounded-lg shadow-lg px-6 py-6 w-full max-w-sm">
          <h3 class="text-lg font-bold mb-4">Add Delegate</h3>
          <form @submit.prevent="submitAddDelegate">
            <div class="mb-3">
              <label class="block text-xs font-semibold text-gray-700 mb-1">Ticket Type</label>
              <select v-model="formState.addTicketId" class="w-full border rounded px-2 py-1 text-sm" required>
                <option disabled value="">Select ticket</option>
                <option value="63">Child 2-16</option>
                <option value="64">Concession 0-1</option>
                <option value="61">Adult</option>
              </select>
            </div>
            <!-- Main Conference -->
            <div v-if="formState.addTicketId === '61'">
              <div class="mb-3">
                <label class="block text-xs font-semibold text-gray-700 mb-1">First Name</label>
                <input v-model="formState.addFirstName" maxlength="20" class="w-full border rounded px-2 py-1 text-sm" required />
              </div>
              <div class="mb-4">
                <label class="block text-xs font-semibold text-gray-700 mb-1">Last Name</label>
                <input v-model="formState.addLastName" maxlength="20" class="w-full border rounded px-2 py-1 text-sm" required />
              </div>
            </div>
            <!-- Kids Conference & Concessions -->
            <div v-else-if="formState.addTicketId === '63' || formState.addTicketId === '64'">
              <div class="mb-3">
                <label class="block text-xs font-semibold text-gray-700 mb-1">First Name</label>
                <input v-model="formState.addFirstName" maxlength="20" class="w-full border rounded px-2 py-1 text-sm" required />
              </div>
              <div class="mb-3">
                <label class="block text-xs font-semibold text-gray-700 mb-1">Last Name</label>
                <input v-model="formState.addLastName" maxlength="20" class="w-full border rounded px-2 py-1 text-sm" required />
              </div>
              <div class="mb-3">
                <label class="block text-xs font-semibold text-gray-700 mb-1">Age</label>
                <input v-model="formState.addAge" type="number" min="0" class="w-full border rounded px-2 py-1 text-sm" required />
              </div>
              <div class="mb-4">
                <label class="block text-xs font-semibold text-gray-700 mb-1">Gender</label>
                <select v-model="formState.addGender" class="w-full border rounded px-2 py-1 text-sm" required>
                  <option disabled value="">Select gender</option>
                  <option value="F">Girl</option>
                  <option value="M">Boy</option>
                </select>
              </div>
            </div>
            <div class="flex justify-end gap-2">
              <button type="button" @click="formState.showAddDelegate = false" class="px-3 py-1 rounded bg-gray-200 hover:bg-gray-300 text-gray-700 text-sm">Cancel</button>
              <button type="submit" class="px-3 py-1 rounded bg-green-600 hover:bg-green-700 text-white text-sm font-semibold">Add</button>
            </div>
          </form>
        </div>
      </div>
    </transition>
  </div>
</main>
</template>

<script>
import axios from 'axios'
import { defineComponent, onMounted, computed, reactive, ref } from 'vue'
import { useRoute } from 'vue-router'
import responseHelper from '@/helpers/response.helper'
// import SuccessMessage from '@/components/SuccessMessage.vue'
import ErrorMessage from '@/components/ErrorMessage.vue'
import { XMarkIcon, CheckIcon, QuestionMarkCircleIcon, EllipsisVerticalIcon } from '@heroicons/vue/20/solid'

export default defineComponent({
  components: {
    // SuccessMessage,
    ErrorMessage,
    XMarkIcon,
    QuestionMarkCircleIcon,
    CheckIcon,
    EllipsisVerticalIcon
  },
  setup () {
    const route = useRoute()
    const registrationGuid = ref(route.params.guid)

    const formState = reactive({
      registration: undefined,
      loadError: responseHelper.noError,
      formSubmitted: false,
      formError: responseHelper.noError,
      successMessage: '',
      loading: false,
      showMakeChild: false,
      showUpdateColour: false,
      showAddDelegate: false,
      selectedDelegate: null,
      selectedColour: '',
      selectedColourCode: '',
      addTicketId: '',
      addFirstName: '',
      addLastName: '',
      addAge: '',
      addGender: '',
      actionMenuFor: null
    })

    const changeRegistered = async (item) => {
      formState.loading = true
      try {
        const response = await axios.post(
          'pub/registrations/register',
          {
            guid: registrationGuid.value,
            delegateId: item.id
          }
        )
        if (response?.data?.success && response?.data?.result) {
          formState.registration = response.data.result
        } else {
          formState.loadError = responseHelper.unexpectedResult
        }
      } catch (error) {
        formState.loadError = responseHelper.getMessageV2(error)
      } finally {
        formState.loading = false
      }
    }

    onMounted(() => {
      axios.post(
        'pub/registrations/verify',
        {
          guid: registrationGuid.value
        }
      ).then((response) => {
        if (response?.data?.success && response?.data?.result) {
          formState.registration = response.data.result
        } else {
          formState.loadError = responseHelper.unexpectedResult
        }
      }).catch((error) => {
        formState.loadError = responseHelper.getMessageV2(error)
      })
    })

    const eventIdList = computed(() => {
      var eventId = 0
      if (formState && formState.registration && formState.registration.delegates.length > 0) {
        for (var i = 0; i < formState.registration.delegates.length; i++) {
          eventId = formState.registration.delegates[i].eventId
        }
      }
      return eventId
    })

    const sortedDelegates = computed(() => {
      if (!formState.registration || !formState.registration.delegates) {
        return []
      }

      return [...formState.registration.delegates].sort((a, b) => {
        const aName = `${a.lastName || ''} ${a.firstName || ''}`.trim().toLowerCase()
        const bName = `${b.lastName || ''} ${b.firstName || ''}`.trim().toLowerCase()
        return aName.localeCompare(bName)
      })
    })

    const formatAgeGender = (item) => {
      const parts = []
      if (item.age) parts.push('Age ' + item.age)
      if (item.gender) parts.push(item.gender === 'F' ? 'Girl' : item.gender === 'M' ? 'Boy' : item.gender)
      if (parts.length === 0) return item.child ? 'Child' : 'Adult'
      return parts.join(' / ')
    }

    // Open the Make Child modal
    const beginMakeChild = (delegate) => {
      formState.selectedDelegate = { ...delegate }
      formState.showMakeChild = true
      formState.actionMenuFor = null
    }

    // Save Make Child changes
    const submitMakeChild = async () => {
      formState.loading = true
      try {
        const response = await axios.post('pub/registrations/child', {
          guid: registrationGuid.value,
          delegateId: formState.selectedDelegate.id,
          firstName: formState.selectedDelegate.firstName,
          lastName: formState.selectedDelegate.lastName,
          age: formState.selectedDelegate.age,
          gender: formState.selectedDelegate.gender
        })
        // Refresh registration data
        // const response = await axios.post('pub/registrations/verify', { guid: registrationGuid.value })
        if (response?.data?.success && response?.data?.result) {
          formState.registration = response.data.result
        }
        formState.showMakeChild = false
      } catch (e) {
        formState.formError = responseHelper.getMessageV2(e)
      } finally {
        formState.loading = false
      }
    }

    // Open the Update Colour modal
    const beginUpdateColour = (delegate) => {
      formState.selectedDelegate = { ...delegate }
      formState.selectedColour = delegate.colour || ''
      formState.selectedColourCode = delegate.colourCode || ''
      formState.showUpdateColour = true
    }

    // Save Update Colour changes
    const submitUpdateColour = async () => {
      formState.loading = true
      try {
        const response = await axios.post('pub/registrations/colour', {
          guid: registrationGuid.value,
          delegateId: formState.selectedDelegate.id,
          colour: formState.selectedColour,
          colourCode: formState.selectedColourCode
        })
        // Refresh registration data
        // const response = await axios.post('pub/registrations/verify', { guid: registrationGuid.value })
        if (response?.data?.success && response?.data?.result) {
          formState.registration = response.data.result
        }
        formState.showUpdateColour = false
      } catch (e) {
        formState.formError = responseHelper.getMessageV2(e)
      } finally {
        formState.loading = false
      }
    }

    // Add Delegate submit handler
    const submitAddDelegate = async () => {
      formState.loading = true
      try {
        if (formState.addTicketId === '61') {
          await axios.post('pub/registrations/add-delegate', {
            guid: registrationGuid.value,
            ticketId: 61,
            firstName: formState.addFirstName,
            lastName: formState.addLastName,
            child: false
          })
        } else if (
          formState.addTicketId === '63' ||
          formState.addTicketId === '64'
        ) {
          await axios.post('pub/registrations/add-delegate', {
            guid: registrationGuid.value,
            ticketId: Number(formState.addTicketId),
            firstName: formState.addFirstName,
            lastName: formState.addLastName,
            child: true,
            age: formState.addAge,
            gender: formState.addGender
          })
        }
        // Refresh registration data
        const response = await axios.post('pub/registrations/verify', { guid: registrationGuid.value })
        if (response?.data?.success && response?.data?.result) {
          formState.registration = response.data.result
        }
        formState.showAddDelegate = false
        formState.addTicketId = ''
        formState.addFirstName = ''
        formState.addLastName = ''
        formState.addAge = ''
        formState.addGender = ''
      } catch (e) {
        formState.formError = responseHelper.getMessageV2(e)
      } finally {
        formState.loading = false
      }
    }

    // Example Actions button handler
    const openDelegateActions = (item) => {
      formState.actionMenuFor = formState.actionMenuFor === item.id ? null : item.id
    }

    return {
      formState,
      registrationGuid,
      changeRegistered,
      eventIdList,
      sortedDelegates,
      formatAgeGender,
      beginMakeChild,
      submitMakeChild,
      openDelegateActions,
      beginUpdateColour,
      submitUpdateColour,
      submitAddDelegate
    }
  }
})
</script>

<style>
.fade-enter-active, .fade-leave-active {
  transition: opacity 0.2s;
}
.fade-enter-from, .fade-leave-to {
  opacity: 0;
}
</style>
